import * as THREE from 'three'
import Terrain from '../terrain'
import Materials, { MaterialType } from '../terrain/mesh/materials'
import Inventory from '../inventory'
import { InventoryItemType, isBlockType } from '../items'
import { blockIcons } from '../assets/block-icons'

interface DropEntity {
  mesh: THREE.Mesh
  type: InventoryItemType
  velocity: THREE.Vector3
  spawnTime: number
  grounded: boolean
  collected: boolean
  groundY: number
}

export default class BlockDropManager {
  constructor(scene: THREE.Scene, inventory: Inventory, terrain: Terrain) {
    this.scene = scene
    this.inventory = inventory
    this.terrain = terrain
    this.materials = new Materials()
  }

  scene: THREE.Scene
  inventory: Inventory
  terrain: Terrain
  materials: Materials
  itemMaterials = new Map<InventoryItemType, THREE.MeshBasicMaterial>()
  textureLoader = new THREE.TextureLoader()
  drops: DropEntity[] = []

  // per-frame scratch objects (no allocations in update)
  dirScratch = new THREE.Vector3()
  toRemove: number[] = []

  // config
  dropScale = 0.3
  pickupRange = 2.0
  magnetRange = 3.5
  magnetSpeed = 12
  despawnTime = 60000 // 60 seconds
  gravity = -18
  bounceRestitution = 0.3
  maxDrops = 64

  materialTypeMap = [
    MaterialType.grass,
    MaterialType.sand,
    MaterialType.tree,
    MaterialType.leaf,
    MaterialType.dirt,
    MaterialType.stone,
    MaterialType.coal,
    MaterialType.wood,
    MaterialType.diamond,
    MaterialType.quartz,
    MaterialType.glass,
    MaterialType.bedrock,
    MaterialType.craftingTable,
    MaterialType.water,
    MaterialType.lava,
    MaterialType.obsidian,
    MaterialType.cobblestone,
    MaterialType.tnt
  ]

  getDropMaterial(type: InventoryItemType) {
    if (isBlockType(type)) {
      return this.materials.get(this.materialTypeMap[type])
    }

    const cached = this.itemMaterials.get(type)
    if (cached) return cached

    const texture = this.textureLoader.load(blockIcons[type])
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1
    })
    this.itemMaterials.set(type, material)
    return material
  }

  /**
   * Spawn a drop entity at the given position.
   */
  spawn(position: THREE.Vector3, type: InventoryItemType): void {
    // limit max drops
    if (this.drops.length >= this.maxDrops) {
      const oldest = this.drops.shift()
      if (oldest) {
        this.scene.remove(oldest.mesh)
        // each drop owns its own geometry (created in spawn) — must dispose.
        // material is shared from this.materials so we leave it alone.
        oldest.mesh.geometry.dispose()
      }
    }

    const geometry = new THREE.BoxGeometry(
      this.dropScale,
      this.dropScale,
      this.dropScale
    )
    const material = this.getDropMaterial(type)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.copy(position)
    mesh.castShadow = false
    mesh.receiveShadow = false

    // random upward + sideways ejection
    const velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 3,
      4 + Math.random() * 2,
      (Math.random() - 0.5) * 3
    )

    this.scene.add(mesh)

    this.drops.push({
      mesh,
      type,
      velocity,
      spawnTime: performance.now(),
      grounded: false,
      collected: false,
      groundY: position.y
    })
  }

  /**
   * Update all drop entities. Call each frame.
   */
  update(delta: number, playerPos: THREE.Vector3): void {
    const now = performance.now()
    const toRemove = this.toRemove
    toRemove.length = 0

    for (let i = 0; i < this.drops.length; i++) {
      const drop = this.drops[i]

      // despawn check
      if (now - drop.spawnTime > this.despawnTime) {
        toRemove.push(i)
        continue
      }

      // already collected — animate toward player then remove
      if (drop.collected) {
        const dir = this.dirScratch
          .subVectors(playerPos, drop.mesh.position)
        const dist = dir.length()
        if (dist < 0.3) {
          toRemove.push(i)
          continue
        }
        dir.normalize().multiplyScalar(this.magnetSpeed * delta)
        drop.mesh.position.add(dir)
        drop.mesh.scale.multiplyScalar(0.92)
        drop.mesh.rotation.y += 8 * delta
        continue
      }

      // physics
      if (!drop.grounded) {
        drop.velocity.y += this.gravity * delta
        drop.mesh.position.x += drop.velocity.x * delta
        drop.mesh.position.y += drop.velocity.y * delta
        drop.mesh.position.z += drop.velocity.z * delta

        // ground collision — use real terrain elevation
        const terrainY = this.terrain.getElevation(
          drop.mesh.position.x,
          drop.mesh.position.z,
          drop.mesh.position.y
        )
        const groundTop = terrainY + 0.5  // top surface of the ground block
        drop.groundY = groundTop  // update for bobbing later
        if (drop.mesh.position.y <= groundTop + this.dropScale / 2) {
          drop.mesh.position.y = groundTop + this.dropScale / 2
          if (Math.abs(drop.velocity.y) < 1) {
            drop.grounded = true
            drop.velocity.set(0, 0, 0)
          } else {
            drop.velocity.y *= -this.bounceRestitution
            drop.velocity.x *= 0.5
            drop.velocity.z *= 0.5
          }
        }
      }

      // spinning
      drop.mesh.rotation.y += 2.5 * delta

      // bobbing when grounded
      if (drop.grounded) {
        const bob = Math.sin((now - drop.spawnTime) * 0.003) * 0.08
        drop.mesh.position.y = drop.groundY + this.dropScale / 2 + 0.15 + bob
      }

      // proximity pickup check
      const dx = playerPos.x - drop.mesh.position.x
      const dy = (playerPos.y - 1) - drop.mesh.position.y // player feet
      const dz = playerPos.z - drop.mesh.position.z
      const distSq = dx * dx + dy * dy + dz * dz

      if (distSq < this.pickupRange * this.pickupRange) {
        const overflow = this.inventory.addItem(drop.type, 1)
        if (overflow === 0) {
          drop.collected = true
        }
      } else if (distSq < this.magnetRange * this.magnetRange) {
        // magnetic pull
        const dir = this.dirScratch.set(dx, dy, dz).normalize()
        drop.mesh.position.add(
          dir.multiplyScalar(this.magnetSpeed * 0.3 * delta)
        )
      }
    }

    // remove collected/despawned drops (reverse to preserve indices)
    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i]
      this.scene.remove(this.drops[idx].mesh)
      this.drops[idx].mesh.geometry.dispose()
      this.drops.splice(idx, 1)
    }
  }

  /**
   * Clear all drops from the scene.
   */
  clear(): void {
    for (const drop of this.drops) {
      this.scene.remove(drop.mesh)
      drop.mesh.geometry.dispose()
    }
    this.drops = []
  }
}
